import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { RefreshCw, Plus, Loader2, Lock, X, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { suggestSkillsForRole } from '@/services/openaiService';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface SkillsSelectionStepProps {
  onSubmit: (skills: string[]) => void;
  onBack: () => void;
  initialSkills: string[];
  role: string;
  level: 'entry' | 'mid' | 'senior';
  isBasicPlan?: boolean;
}

const SkillsSelectionStep: React.FC<SkillsSelectionStepProps> = ({ 
  onSubmit, 
  onBack, 
  initialSkills,
  role,
  level,
  isBasicPlan = false
}) => {
  const [selectedSkills, setSelectedSkills] = useState<string[]>(initialSkills);
  const [suggestedSkills, setSuggestedSkills] = useState<string[]>([]);
  const [customSkill, setCustomSkill] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPremiumDialog, setShowPremiumDialog] = useState(false);
  
  useEffect(() => {
    fetchAISkillSuggestions();
  }, [role, level]);
  
  const fetchAISkillSuggestions = async () => {
    if (!role) return;
    
    setIsLoading(true);
    try {
      const skills = await suggestSkillsForRole(role, level);
      setSuggestedSkills(skills);
    } catch (error) {
      console.error('Error fetching AI skills:', error);
      toast.error('Could not load skill suggestions');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSkillClick = (skill: string) => {
    if (selectedSkills.includes(skill)) {
      setSelectedSkills(selectedSkills.filter(s => s !== skill));
    } else {
      if (isBasicPlan && selectedSkills.length >= 3) {
        setShowPremiumDialog(true);
        return;
      }
      setSelectedSkills([...selectedSkills, skill]);
    }
  };
  
  const handleAddCustomSkill = () => {
    if (!customSkill.trim()) return;
    
    if (isBasicPlan && selectedSkills.length >= 3) {
      setShowPremiumDialog(true);
      return;
    }

    if (selectedSkills.includes(customSkill.trim())) {
      toast.warning('This skill is already added');
      return;
    }

    setSelectedSkills([...selectedSkills, customSkill.trim()]);
    setCustomSkill('');
  };
  
  const regenerateSkills = () => {
    fetchAISkillSuggestions();
  };
  
  const handleSubmit = () => {
    if (selectedSkills.length === 0) {
      setError('Please select at least one skill');
      return;
    }
    
    setError('');
    onSubmit(selectedSkills);
  };
  
  const fadeInVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5
      }
    })
  };
  
  return (
    <div className="bg-white/90 rounded-lg border border-[#9b87f5]/30 shadow-lg w-full max-w-3xl px-8 py-10 backdrop-blur-sm">
      <motion.h1 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-3xl font-bold text-center mb-3 text-[#1A1F2C]"
      >
        Select Skills to Assess
      </motion.h1>
      
      <motion.p 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { delay: 0.2 } }}
        className="text-center text-[#1A1F2C]/80 mb-8"
      >
        Choose {isBasicPlan ? "up to 3 skills" : "skills"} that are most important for the {level} {role} position.
      </motion.p>
      
      <div className="mb-4 flex justify-end">
        <Button 
          variant="outline" 
          size="sm" 
          className="text-[#1A1F2C] border-[#9b87f5]/30 hover:bg-[#9b87f5]/10 hover:text-[#1A1F2C] bg-white"
          onClick={regenerateSkills}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 size={14} className="mr-2 animate-spin" /> 
              Generating...
            </>
          ) : (
            <>
              <RefreshCw size={14} className="mr-2" /> 
              Regenerate Skills
            </>
          )}
        </Button>
      </div>
      
      <div className="bg-[#F8F7FF] rounded-lg p-6 mb-8 border border-[#9b87f5]/20">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 text-[#9b87f5] animate-spin mb-4" />
            <p className="text-[#1A1F2C]/70">Analyzing top skills for {level} {role}...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <AnimatePresence>
              {suggestedSkills.map((skill, index) => (
                <motion.div
                  key={skill}
                  custom={index}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={fadeInVariants}
                >
                  <Button
                    variant={selectedSkills.includes(skill) ? "purple" : "outline"}
                    className={`text-sm min-h-[40px] w-full flex items-center justify-center break-words whitespace-normal ${
                      selectedSkills.includes(skill) 
                        ? 'bg-gradient-to-r from-[#9b87f5] to-[#7662DA] text-white hover:from-[#8A76E5] hover:to-[#6552CA]' 
                        : 'bg-white border-[#9b87f5]/30 text-[#1A1F2C] hover:bg-[#9b87f5]/10 hover:text-[#1A1F2C]'
                    }`}
                    onClick={() => handleSkillClick(skill)}
                    title={skill}
                  >
                    <span className="px-2 py-1 text-center">{skill}</span>
                  </Button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
        
        <div className="mt-6 relative">
          <div className="flex gap-2">
            <Input
              className="bg-white border-[#9b87f5]/30 text-[#1A1F2C] placeholder:text-[#1A1F2C]/50 focus-visible:ring-[#9b87f5]/50"
              placeholder="Add a custom skill..."
              value={customSkill}
              onChange={(e) => setCustomSkill(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCustomSkill();
                }
              }}
            />
            <Button 
              variant="purple"
              className="bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA]"
              onClick={handleAddCustomSkill}
            >
              <Plus size={16} />
            </Button>
          </div>
          
          {isBasicPlan && selectedSkills.length >= 3 && (
            <div className="absolute right-0 top-[-30px]">
              <Badge variant="premium" className="bg-gradient-to-r from-[#FFB168] to-[#FF8C37] text-white">
                Premium Feature
              </Badge>
            </div>
          )}

          {isBasicPlan && selectedSkills.length >= 3 && (
            <div className="mt-4 bg-orange-50 border border-orange-200 rounded-md p-3 text-orange-800 text-xs">
              <div className="flex items-start gap-2">
                <Lock className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p>Adding more than 3 skills requires a Premium or Agency subscription.</p>
                  <Link 
                    to="/credits" 
                    className="flex items-center gap-1 mt-2 text-[#7662DA] font-medium hover:underline"
                  >
                    <ExternalLink size={14} />
                    Upgrade your plan now
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {selectedSkills.length > 0 && (
        <div className="mb-8">
          <h3 className="text-[#1A1F2C]/80 text-sm mb-2">Selected Skills:</h3>
          <div className="flex flex-wrap gap-2">
            {selectedSkills.map((skill) => (
              <Badge 
                key={skill} 
                variant="selected"
                className="cursor-pointer bg-[#9b87f5]/20 text-[#1A1F2C] hover:bg-[#9b87f5]/30 border border-[#9b87f5]/30 flex items-center gap-1.5 pr-2"
              >
                <span>{skill}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSkillClick(skill);
                  }}
                  className="hover:bg-[#9b87f5]/30 rounded-full p-0.5 transition-colors"
                >
                  <X size={14} />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      )}
      
      {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
      
      <div className="flex justify-between">
        <Button 
          variant="outline" 
          className="border-[#9b87f5]/30 bg-white hover:bg-[#9b87f5]/10 text-[#1A1F2C]/70 hover:text-[#1A1F2C]"
          onClick={onBack}
        >
          Back
        </Button>
        
        <Button 
          variant="purple"
          className="bg-gradient-to-r from-[#9b87f5] to-[#7662DA] hover:from-[#8A76E5] hover:to-[#6552CA] shadow-md"
          onClick={handleSubmit}
        >
          Continue
        </Button>
      </div>

      <Dialog open={showPremiumDialog} onOpenChange={setShowPremiumDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-yellow-500" />
              Premium Feature
            </DialogTitle>
            <DialogDescription>
              Adding more than 3 skills is available on the Premium and Agency plans. Upgrade to add more skills and unlock additional features.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-6">
            <Button
              variant="outline"
              onClick={() => setShowPremiumDialog(false)}
            >
              Maybe Later
            </Button>
            <Button
              className="bg-gradient-to-r from-[#FFB168] to-[#FF8C37] text-white hover:from-[#FFA158] hover:to-[#FF7C27]"
              onClick={() => {
                setShowPremiumDialog(false);
                window.location.href = '/credits';
              }}
            >
              Upgrade Now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SkillsSelectionStep;
